import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Flex, Box } from 'rebass/styled-components';
import PropTypes from 'prop-types';
import { Loader, MessageBlock } from '@getro/rombo';
import UserSchema from 'schemas/user';
import strings from 'strings';
import toast from 'react-hot-toast';
import { activeNetworkSelector } from 'redux/selectors';
import { AdminDataTable } from './AdminDataTable';
import RemoveAdminModal from '../adminPanel/removeAdminModal';

export const AdminTable = ({
  onRemoveAdmin = null,
  onRemoveAdminClick = null,
  onEditRoleClick = null,
  onResendInvitation = () => {},
  admins = [],
  currentUser,
  isResendingInvitation = false,
  isDeletingAdmin = false,
  loadingAdminsError,
  isLoadingAdmins,
}) => {
  const activeNetwork = useSelector(activeNetworkSelector);
  const canManageAdmins = !!activeNetwork?.isManager;
  const urlParams = new URLSearchParams(window.location.search);
  const inviteModalOpen = urlParams.has('invite-admin') || urlParams.has('invite-manager');
  const [payload, setActionPayload] = useState(inviteModalOpen ? { action: 'invite' } : null);
  const [userToResendInvitation, setUserToResendInvitation] = useState(null);

  const handleSetPayload = (data, adminAction) => {
    if (adminAction === 'remove' && onRemoveAdminClick?.(data)) return;
    if (adminAction === 'edit-role' && onEditRoleClick?.(data)) return;

    setActionPayload({
      data,
      action: adminAction,
    });
  };

  if (loadingAdminsError) return <MessageBlock data-testid="fetch-error">{strings.genericError}</MessageBlock>;

  if (isLoadingAdmins)
    return (
      <Flex mt="40px" justifyContent="center" width="100%">
        <Loader />
      </Flex>
    );

  return (
    <>
      <Box m="0 auto">
        <AdminDataTable
          admins={admins ?? []}
          onRemoveAdmin={canManageAdmins && onRemoveAdmin ? handleSetPayload : null}
          onResendInvitation={async (user) => {
            if (user.role !== 'ally' && !canManageAdmins) return;
            setUserToResendInvitation(user);
            const result = await onResendInvitation(user);
            if (result?.error) {
              setUserToResendInvitation(null);
              return;
            }
            toast('Invite resent', {
              icon: 'success',
              id: 'resend-network-invite',
            });
            setUserToResendInvitation(null);
          }}
          isResendingInvitation={isResendingInvitation}
          onUpdateRole={onEditRoleClick ? handleSetPayload : null}
          userToResendInvitation={userToResendInvitation}
        />
      </Box>
      {canManageAdmins && onRemoveAdmin && (
        <RemoveAdminModal
          user={payload?.data}
          currentUser={currentUser}
          network={activeNetwork}
          isSubmitting={isDeletingAdmin}
          modalOpen={payload?.action === 'remove'}
          onCancel={() => setActionPayload(null)}
          onDeleteAdmin={async () => {
            const result = await onRemoveAdmin(payload.data);
            if (result?.error) return;
            setActionPayload(null);
          }}
        />
      )}
    </>
  );
};

AdminTable.propTypes = {
  isDeletingAdmin: PropTypes.bool,
  isResendingInvitation: PropTypes.bool,
  currentUser: UserSchema.isRequired,
  admins: PropTypes.array,
  onRemoveAdmin: PropTypes.func,
  onRemoveAdminClick: PropTypes.func,
  onEditRoleClick: PropTypes.func,
  onResendInvitation: PropTypes.func,
  loadingAdminsError: PropTypes.bool.isRequired,
  isLoadingAdmins: PropTypes.bool.isRequired,
};

AdminTable.defaultProps = {
  isDeletingAdmin: false,
  isResendingInvitation: false,
  admins: [],
  onRemoveAdmin: null,
  onRemoveAdminClick: null,
  onEditRoleClick: null,
  onResendInvitation: () => {},
};
